/**
 * AforoCard · Sprint 4.1 · v2.9.1
 *
 * Visualiza el `aforo_report` del motor: serie mensual del cap dinámico
 * (LTC/LTV/línea contractual) vs saldo del puente y draws reales. Highlights
 * en meses donde el cap recortó la disposición.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyzerAPI } from '@/lib/api';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot,
} from 'recharts';

function fmt(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function fmtShort(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
}

export function AforoCard({ scenarioId, projectId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['analyzer-run', scenarioId, projectId],
    queryFn: async () => (await analyzerAPI.preview({ scenario_id: scenarioId, project_id: projectId })).data,
    enabled: !!scenarioId && !!projectId,
    staleTime: 30000,
  });

  const chartData = useMemo(() => {
    if (!data) return [];
    const caps = data.aforo_report?.monthly_caps || [];
    const monthly = data.monthly_schedule || [];
    return caps.map((cap, i) => {
      const m = monthly[i] || {};
      return {
        month: cap.month,
        effective_cap: cap.effective_cap,
        ltc_cap: cap.ltc_cap,
        ltv_cap: cap.ltv_cap,
        credit_line_cap: cap.credit_line_cap,
        loan_balance: m.loan_balance || 0,
        loan_draw: m.loan_draw || 0,
      };
    });
  }, [data]);

  const events = data?.aforo_report?.events || [];
  const totalCapped = data?.aforo_report?.total_capped || 0;
  const params = data?.aforo_report?.params || {};

  if (isLoading) return <div className="rounded-2xl border border-border/40 bg-card p-6 text-xs text-muted-foreground" data-testid="aforo-card-loading">Cargando aforo…</div>;

  // Sólo mostrar cuando hay línea autorizada > 0 (no aplica para escenarios 100% equity)
  if (!chartData.length || !(params.credit_line > 0)) return null;

  const cappedMonths = new Set(events.map(e => e.month));
  const hasCaps = events.length > 0;
  const yMax = Math.max(
    ...chartData.map(d => Math.max(d.effective_cap || 0, d.loan_balance || 0, d.loan_draw || 0)),
    params.credit_line || 0,
  );

  return (
    <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="aforo-card">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#1E293B' }}>
            Aforo dinámico del puente (Sprint 4.1 · v2.9.1)
          </h3>
          <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
            Cap efectivo = <strong>min(LTC × cost, LTV × valor, línea)</strong>.
            LTC = {(params.aforo_ltc * 100).toFixed(0)}% · LTV = {(params.aforo_ltv * 100).toFixed(0)}% · Línea = {fmt(params.credit_line)}
          </p>
        </div>
        <div
          className="flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-1 rounded"
          style={{
            color: hasCaps ? '#DC2626' : '#16A34A',
            background: hasCaps ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
          }}
          data-testid="aforo-badge"
        >
          {hasCaps ? <AlertTriangle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          {hasCaps ? `${events.length} meses capados · ${fmt(totalCapped)}` : 'Sin cortes'}
        </div>
      </div>

      <div style={{ width: '100%', height: 260 }} data-testid="aforo-chart">
        <ResponsiveContainer>
          <ComposedChart data={chartData} margin={{ top: 8, right: 24, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} label={{ value: 'Mes', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#94A3B8' }} />
            <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={fmtShort} width={60} domain={[0, yMax * 1.05]} />
            <Tooltip
              formatter={(value, name) => [fmt(value), name]}
              labelFormatter={(m) => `Mes ${m}`}
              contentStyle={{ background: 'white', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 11 }}
            />
            <Bar dataKey="loan_balance" name="Saldo puente" fill="rgba(79,70,229,0.28)" barSize={16} />
            <Bar dataKey="loan_draw" name="Disposición mes" fill="rgba(3,105,161,0.7)" barSize={16} />
            <Line dataKey="effective_cap" name="Cap efectivo (LTC/LTV/línea)" stroke="#DC2626" strokeWidth={2} dot={false} />
            <Line dataKey="ltc_cap" name="Cap LTC" stroke="#F59E0B" strokeWidth={1} strokeDasharray="4 4" dot={false} />
            <Line dataKey="ltv_cap" name="Cap LTV" stroke="#7C3AED" strokeWidth={1} strokeDasharray="4 4" dot={false} />
            {events.map((e, i) => (
              <ReferenceDot key={i} x={e.month} y={e.capped_to} r={5} fill="#DC2626" stroke="white" strokeWidth={1.5} isFront ifOverflow="visible" />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {hasCaps && (
        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
            Meses recortados
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(148,163,184,0.25)' }}>
                  <th className="text-left py-1 px-2 font-semibold" style={{ color: '#94A3B8' }}>Mes</th>
                  <th className="text-right py-1 px-2 font-semibold" style={{ color: '#94A3B8' }}>Solicitado</th>
                  <th className="text-right py-1 px-2 font-semibold" style={{ color: '#94A3B8' }}>Liberado</th>
                  <th className="text-right py-1 px-2 font-semibold" style={{ color: '#94A3B8' }}>Cap efectivo</th>
                  <th className="text-right py-1 px-2 font-semibold" style={{ color: '#94A3B8' }}>Recorte</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 15).map((e, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
                    <td className="py-1 px-2" style={{ color: '#334155' }}>M{e.month}</td>
                    <td className="py-1 px-2 text-right font-mono" style={{ color: '#334155' }}>{fmt(e.requested)}</td>
                    <td className="py-1 px-2 text-right font-mono" style={{ color: '#0369A1' }}>{fmt(e.capped_to)}</td>
                    <td className="py-1 px-2 text-right font-mono" style={{ color: '#7C3AED' }}>{fmt(e.cap_dynamic)}</td>
                    <td className="py-1 px-2 text-right font-mono font-semibold" style={{ color: '#DC2626' }}>{fmt(e.requested - e.capped_to)}</td>
                  </tr>
                ))}
                {events.length > 15 && (
                  <tr>
                    <td colSpan={5} className="py-1 px-2 text-center text-[10px]" style={{ color: '#94A3B8' }}>
                      … y {events.length - 15} meses más
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] mt-2" style={{ color: '#64748B' }}>
            <strong>Recomendación:</strong> aumenta LTC/LTV con el banco, o difiere costos, o inyecta más equity — el motor emite alerta separada
            con el detalle.
          </div>
        </div>
      )}
    </div>
  );
}
